/* ============================================================================
   ecofuego-lp-motion.js — scroll-in motion for the landing-page sections.

   Small, deliberate, and off by default in three situations. The whole point
   is that the page feels alive without anyone noticing why.

   THE THREE RULES THIS IS BUILT ON
   ------------------------------------------------------------------------
   1. FAIL OPEN. The hidden state is added BY THIS SCRIPT, never by the
      stylesheet. If the JS is blocked, errors, or simply never runs, every
      element is visible — the default is a working page, not a blank one.
      A stylesheet that hides content and waits for JS to reveal it is the
      single most common way a page ships invisible.

   2. NOTHING ALREADY ON SCREEN ANIMATES. Anything in the viewport at load
      is marked visible immediately, with no transition. Fading in the hero
      delays the largest contentful paint for no benefit and makes a fast
      page feel slower than it is.

   3. NEVER TRANSFORM AN ANCESTOR OF position: sticky OR fixed. A transform
      creates a containing block, so a transformed parent silently breaks
      any sticky or fixed descendant. That is why the bundle builder and the
      sticky bar are excluded rather than animated — the builder's summary
      panel is sticky, and it would simply stop sticking.

   Transform and opacity only: both composite on the GPU, neither triggers
   layout, so this costs nothing on a mid-range phone.
   ========================================================================== */
(function () {
  'use strict';

  /* Every LP section renders lp-head, so this file arrives once per section.
     Run once. */
  if (window.__efMotion) return;
  window.__efMotion = true;

  function start() {
    var reduced = window.matchMedia &&
                  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;

    /* What moves. Deliberately a short list: the things a reader's eye lands
       on. Animating every element makes a page feel busy rather than alive. */
    var SELECTOR = [
      '.ef-lp__eyebrow',
      '.ef-lp__h1',
      '.ef-lp__h2',
      '.ef-lp__h3',
      '.ef-lp__rule',
      '.ef-lp__lead',
      '.ef-lp__body',
      '.ef-lp__mediawrap',
      '.ef-lp__q',
      '.ef-lp__step',
      '.ef-lp__situ',
      '.ef-lp__tier',
      '.ef-lp__badges',
      '.ef-lp__ctawrap',
      '.ef-lp__chores li'
    ].join(',');

    /* Excluded, for the reasons in the header. */
    var EXCLUDE = '.ef-lp--hero, .ef-bld, .ef-lp-sticky, .ef-lp__ugc';

    var targets = Array.prototype.filter.call(
      document.querySelectorAll(SELECTOR),
      function (n) { return !n.closest(EXCLUDE); }
    );
    if (!targets.length) return;

    var vh = window.innerHeight || document.documentElement.clientHeight;

    targets.forEach(function (n) {
      var top = n.getBoundingClientRect().top;
      if (top < vh * 0.82) {
        /* Already on screen. Visible now, no transition, no cost. */
        n.classList.add('ef-anim', 'ef-anim--in', 'ef-anim--instant');
      } else {
        n.classList.add('ef-anim');
      }
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;

        /* Stagger by position within the parent, capped so a long list never
           leaves the last item waiting. 100ms against a 900ms travel reads as
           one movement arriving in parts; much more and it becomes a sequence,
           which is a different and more annoying thing. 120ms against the
           1100ms travel keeps that ratio. */
        var sibs = e.target.parentNode
          ? Array.prototype.filter.call(e.target.parentNode.children,
              function (c) { return c.classList.contains('ef-anim'); })
          : [];
        var i = Math.min(sibs.indexOf(e.target), 4);
        e.target.style.transitionDelay = (i > 0 ? i * 120 : 0) + 'ms';

        e.target.classList.add('ef-anim--in');
        io.unobserve(e.target);          /* once only — never re-animates */
      });
      /* A NEGATIVE bottom margin shrinks the root, so the element has to
         travel further up the screen before it counts as intersecting —
         bigger number, later trigger. -4% fired almost the moment anything
         crossed the fold, which is what read as "too early". -18% waits
         until the element is properly on screen and the movement then plays
         where the eye already is. */
      /* THRESHOLD 0, NOT 0.01 — this one is a bug fix, not a preference.
         intersectionRatio is intersected-area / element-area, so an element
         with no area can never reach 0.01 and never fires. `.ef-lp__mediawrap`
         is exactly that until the image inside it loads: `.ef-lp__ph img` is
         width:100% / height:auto, which is zero-height before the file
         arrives. That produced a deadlock — the wrapper stays at opacity 0
         because it has no height, and a lazily-loaded image inside a fully
         transparent ancestor is not fetched, so it never gets one. The heat
         section on the product templates rendered as an empty black block
         because of it. With threshold 0, isIntersecting is true as soon as
         the boxes overlap at all, area or none. */
    }, { rootMargin: '0px 0px -18% 0px', threshold: 0 });

    targets.forEach(function (n) {
      if (!n.classList.contains('ef-anim--in')) io.observe(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
